import { useLoaderData } from '@remix-run/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import {
	PARCEL_SHIPPING_FEE_IN_PRICE,
	FREE_SHIPPING_LIMIT_IN_PRICE,
	HOME_DELIVERY_FEE_IN_PRICE,
	RETURN_SHIPPING_FEE_IN_PRICE,
} from '#app/constants/shipping-rates-price'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { type loader } from '#app/routes/_public+/product+/$styleNumber'
import { getLokalisedString } from '#app/utils/localisation/lokalisation-utilities'

import { type StyleText } from '#types/lokalisation'
import SizeGuideDialog from './actions/size-guide-dialog'

interface MaterialCT {
	material: StyleText
	percentage: string
	type: StyleText
}

interface MaterialProps {
	material: {
		obj: {
			value: MaterialCT[]
		}
	}
}
const ProductInformation = () => {
	const loaderData = useLoaderData<typeof loader>()
	const { trackAmplitudeEvent } = useAmplitudeContext()
	let { t } = useTranslation('product_details_page')

	const locale = loaderData?.locale
	const productData = loaderData?.productData

	if (!productData || !locale) {
		return null
	}

	const brandKey = productData.productInfo.masterVariant.attributes?.find(
		item => item.name === 'brand',
	)?.value

	const material = productData.productInfo.masterVariant.attributes?.find(
		item => item.name === 'material_composition',
	)

	const description = getLokalisedString(
		productData.productInfo?.description as StyleText,
		locale,
	)

	return (
		<Accordion className="flex flex-col gap-2 pt-2 md:pt-0" type="multiple">
			{description && (
				<AccordionItem value={'description'} key={'description'}>
					<AccordionTrigger
						onClickCapture={() =>
							trackAmplitudeEvent('click product description')
						}
					>
						<PlentyBody size="md">{t('description_header')}</PlentyBody>
					</AccordionTrigger>
					<AccordionContent>
						<Description description={description} />
					</AccordionContent>
				</AccordionItem>
			)}
			{material && (
				<AccordionItem value="material" key={material.name}>
					<AccordionTrigger
						onClickCapture={() => trackAmplitudeEvent('click product material')}
					>
						<PlentyBody size="md">{t('material_header')}</PlentyBody>
					</AccordionTrigger>
					<AccordionContent>
						{material.value && <Material material={material.value} />}
					</AccordionContent>
				</AccordionItem>
			)}
			<AccordionItem value="size" key={'size'}>
				<AccordionTrigger
					onClickCapture={() => trackAmplitudeEvent('click product size ')}
				>
					<PlentyBody size="md">{t('size_&_fit_header')}</PlentyBody>
				</AccordionTrigger>
				<AccordionContent className="flex items-start justify-start text-start">
					<SizeGuideDialog brandKey={brandKey} />
				</AccordionContent>
			</AccordionItem>
			<AccordionItem value="shipping" key={'shipping'}>
				<AccordionTrigger
					onClickCapture={() => trackAmplitudeEvent('click delivery & returns')}
				>
					<PlentyBody size="md">{t('delivery_&_returns_header')}</PlentyBody>
				</AccordionTrigger>
				<AccordionContent className="space-y-2">
					<DeliveryAndReturns />
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}

export default ProductInformation

const Description = ({ description }: { description: string }) => {
	const loaderData = useLoaderData<typeof loader>()
	const { t } = useTranslation('common')
	const splittedDescription = description.split(' - ')
	const heading = splittedDescription.shift()
	const productData = loaderData?.productData
	return (
		<div className="space-y-2">
			<PlentyBody size="md">
				{`${t('style_number')}: ${productData.productInfo.key}`}
			</PlentyBody>

			<PlentyBody size="md">{heading}</PlentyBody>
			<ul className="list-disc">
				{splittedDescription.map(item => (
					<li key={item} className="ml-4">
						<PlentyBody size="md">{item}</PlentyBody>
					</li>
				))}
			</ul>
		</div>
	)
}

const Material = ({ material }: MaterialProps) => {
	const materials = material.obj.value
	let { i18n } = useTranslation('product_details_page')
	return (
		<ul className="list-disc">
			{materials.map((item, index) => (
				<li key={`${index}-${item.percentage}`} className="ml-4">
					<div className="flex w-full flex-row justify-between">
						<PlentyBody size="md">
							{getLokalisedString(item.material, i18n.language)}
						</PlentyBody>
						<PlentyBody size="md">{item.percentage}%</PlentyBody>
					</div>
				</li>
			))}
		</ul>
	)
}

const DeliveryAndReturns = () => {
	let { t } = useTranslation('product_details_page')

	return (
		<div className="space-y-2">
			<PlentyBody size="md" className="font-bold text-black">
				{t('common:delivery_header')}
			</PlentyBody>
			<div>
				<PlentyBody size="md">{t('click_&_collect_at_store')}</PlentyBody>
				<div className="flex flex-row justify-between ">
					<PlentyBody size="md" className="text-beige-100">
						{t('within_24_hours')}
					</PlentyBody>
					<PlentyBody size="md" className="text-beige-100">
						{t('common:free_tag')}
					</PlentyBody>
				</div>
			</div>
			<div>
				<PlentyBody size="md">{t('deliver_to_a_parcel_shop')}</PlentyBody>
				<div className="flex flex-row justify-between ">
					<PlentyBody size="md" className="text-beige-100">
						{t('checkout:ships_in_working_days')}
					</PlentyBody>
					<PlentyBody size="md" className="text-beige-100">
						{t('or_free_over_499', {
							'shipping-price': PARCEL_SHIPPING_FEE_IN_PRICE,
							'free-delivery': FREE_SHIPPING_LIMIT_IN_PRICE,
						})}
					</PlentyBody>
				</div>
			</div>
			<div>
				<PlentyBody size="md">{t('deliver_to_your_home_address')}</PlentyBody>
				<div className="flex flex-row justify-between ">
					<PlentyBody size="md" className="text-beige-100">
						{t('checkout:ships_in_working_days')}
					</PlentyBody>
					<PlentyBody size="md" className="text-beige-100">
						{HOME_DELIVERY_FEE_IN_PRICE} kr
					</PlentyBody>
				</div>
			</div>
			<PlentyBody size="md" className="font-bold text-black">
				{t('return_methods')}
			</PlentyBody>
			<div>
				<div className="flex flex-row justify-between ">
					<PlentyBody size="md" className="text-black-90">
						{t('returns:return_in_store_label')}
					</PlentyBody>
					<PlentyBody size="md" className="text-beige-100">
						{t('common:free_tag')}
					</PlentyBody>
				</div>
			</div>
			<div>
				<div className="flex flex-row justify-between ">
					<PlentyBody size="md" className="text-black-90">
						{t('return_as_a_package')}
					</PlentyBody>
					<PlentyBody size="md" className="text-beige-100">
						{RETURN_SHIPPING_FEE_IN_PRICE} kr
					</PlentyBody>
				</div>
			</div>
		</div>
	)
}
